import React, { useCallback } from 'react';
import moment from 'moment';
import { UseControllerProps } from 'react-hook-form';
import ReactDaterangepicker from '../../../../components/ReactDaterangepicker/ReactDaterangepicker';
import { addModifiers, handleDataExplorerDateTime } from '../../../../shared/helpers';
import { FilterDateRange } from '../../FieldFilters/types';
import { useFilterController } from '../useFilterForm';
import { getInputId } from './getInputId';

import './FilterFields.scss';

interface Props {
  title: string;
  controllerProps: UseControllerProps;
  className?: string;
  placeholder?: string;
}

export const DateRangeField = ({ title, controllerProps, className, placeholder }: Props) => {
  const controller = useFilterController(controllerProps);
  const value = (controller.field.value ?? {}) as FilterDateRange;

  const handleChange = useCallback(
    (dates: moment.Moment[] | string[]) => {
      const [start, end] = dates;
      // handleDataExplorerDateTime returns ISO strings with timezone offset (e.g. 2026-06-04T00:00:00-04:00).
      // Guard the moment branch with an ISO format so a raw moment never stores a non-parseable value.
      const toIso = (date: moment.Moment | string) => (typeof date === 'string' ? date : moment(date).format());
      controller.field.onChange({ start: toIso(start), end: toIso(end) });
    },
    [controller.field]
  );

  const hasValue = !!(value.start || value.end);

  const onReset = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      controller.field.onChange({ start: '', end: '' });
    },
    [controller.field]
  );

  return (
    <div className={[className, 'range-field'].filter(Boolean).join(' ')}>
      <label htmlFor={getInputId(controllerProps.name)} className="range-field__title">
        {title}
      </label>
      <div className="range-field__inputs date-range-field">
        <ReactDaterangepicker
          handleDatesBeforeChange={handleDataExplorerDateTime}
          handleChange={handleChange}
          maxYear={parseInt(moment().format('YYYY'), 10)}
          minYear={parseInt(moment().subtract(10, 'years').format('YYYY'), 10)}
          ranges={true}
          rangesType="dataExplorer"
          showDropdowns={true}
          startDate={value.start || undefined}
          endDate={value.end || undefined}
          placeholder={placeholder}
          modifiers={['data-explorer-date']}
        />
        <button
          type="button"
          aria-label="Clear dates"
          data-testid={`${getInputId(controllerProps.name)}-clear`}
          className={addModifiers('date-range-field__clear', hasValue ? [] : ['hidden'])}
          onClick={onReset}
        />
      </div>
    </div>
  );
};
